<template>
  <div v-if="visible" class="transfer-tray" role="status" aria-live="polite">
    <section v-if="showUpload" class="tray-section">
      <header class="tray-head">
        <div class="tray-title">
          <strong>{{ uploadText }}</strong>
          <span v-if="upload.active" class="tray-muted">ทำงานอื่นต่อได้ระหว่างรอ</span>
        </div>
        <button
          v-if="upload.active"
          type="button"
          class="tray-action danger"
          @click="cancelUpload"
        >
          ยกเลิกทั้งหมด
        </button>
        <button
          v-else-if="upload.canRetry"
          type="button"
          class="tray-action"
          @click="retryFailedUploads()"
        >
          ลองใหม่ทั้งหมด
        </button>
        <button
          v-if="rows.length > 0"
          type="button"
          class="tray-icon"
          :aria-label="expanded ? 'ย่อรายการอัปโหลด' : 'แสดงรายการอัปโหลด'"
          :aria-expanded="expanded ? 'true' : 'false'"
          @click="expanded = !expanded"
        >
          <i :class="expanded ? 'fas fa-chevron-down' : 'fas fa-chevron-up'" aria-hidden="true"></i>
        </button>
        <button
          v-if="!upload.active"
          type="button"
          class="tray-icon"
          aria-label="ปิดแถบอัปโหลด"
          @click="dismissUploadStatus"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </header>
      <div v-if="upload.active" class="tray-progress">
        <div class="tray-progress-bar" :style="{ width: `${upload.percent}%` }"></div>
      </div>
      <ul v-if="expanded && rows.length > 0" class="tray-rows">
        <li v-for="row in rows" :key="row.slot" class="tray-row">
          <span class="tray-row-dot" :class="`is-${row.state}`" aria-hidden="true"></span>
          <span class="tray-row-name" :title="row.name">{{ row.name }}</span>
          <span class="tray-row-status" :class="`is-${row.state}`">{{ rowText(row) }}</span>
          <button
            v-if="row.retryable && row.state === 'failed' && !upload.active"
            type="button"
            class="tray-row-retry"
            @click="retryFailedUploads({ slot: row.slot })"
          >
            ลองใหม่
          </button>
        </li>
      </ul>
    </section>

    <section v-if="download.active" class="tray-section">
      <header class="tray-head">
        <b-spinner small variant="primary" class="mr-2" />
        <div class="tray-title">
          <strong>{{ downloadText }}</strong>
        </div>
      </header>
      <div v-if="download.phase !== 'zip'" class="tray-progress">
        <div class="tray-progress-bar" :style="{ width: `${download.percent}%` }"></div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  downloadMessage,
  rowStatusText,
  uploadMessage,
} from "@/utils/transferToastText";

// How long the "done" message stays after a batch uploads cleanly.
const LINGER_MS = 1500;
// Phones start with the list folded: the tray would cover the page.
const START_EXPANDED =
  typeof window === "undefined" || !window.matchMedia
    ? true
    : !window.matchMedia("(max-width: 575.98px)").matches;

/**
 * Upload tray and download progress, bottom left on every page. Each file
 * of the upload batches in flight (moduleMediaFile.uploadStatus) has a row;
 * the batches can be cancelled, and files they lost can be tried again one
 * by one or all at once. A batch that lost files stays until closed. While an
 * upload runs, leaving the page asks first: the files would be lost.
 */
export default {
  name: "TransferProgressToast",
  data() {
    return { lingering: false, lingerTimer: null, expanded: START_EXPANDED };
  },
  computed: {
    ...mapGetters({
      upload: "moduleMediaFile/getUploadStatus",
      download: "moduleMediaFile/getDownloadStatus",
    }),
    rows() {
      return this.upload.items || [];
    },
    uploadNeedsAttention() {
      const { active, failed, total } = this.upload;
      return !active && total > 0 && failed > 0;
    },
    showUpload() {
      return this.upload.active || this.uploadNeedsAttention || this.lingering;
    },
    visible() {
      return this.showUpload || this.download.active;
    },
    uploadText() {
      return uploadMessage(this.upload);
    },
    downloadText() {
      return downloadMessage(this.download);
    },
  },
  watch: {
    "upload.active"(active) {
      clearTimeout(this.lingerTimer);
      if (active) {
        this.lingering = false;
        window.addEventListener("beforeunload", this.warnBeforeLeaving);
        return;
      }
      window.removeEventListener("beforeunload", this.warnBeforeLeaving);
      // Failures stay until dismissed; a clean or cancelled batch shows its
      // result briefly.
      if (this.upload.total > 0 && this.upload.failed === 0) {
        this.lingering = true;
        this.lingerTimer = setTimeout(() => {
          this.lingering = false;
        }, LINGER_MS);
      }
    },
  },
  methods: {
    ...mapActions({
      retryFailedUploads: "moduleMediaFile/retryFailedUploads",
      dismissUploadStatus: "moduleMediaFile/dismissUploadStatus",
      cancelUpload: "moduleMediaFile/cancelUpload",
    }),
    rowText(row) {
      return rowStatusText(row);
    },
    warnBeforeLeaving(event) {
      event.preventDefault();
      // Chrome still needs returnValue set to show the prompt.
      event.returnValue = "";
    },
  },
  beforeDestroy() {
    clearTimeout(this.lingerTimer);
    window.removeEventListener("beforeunload", this.warnBeforeLeaving);
  },
};
</script>

<style scoped>
.transfer-tray {
  position: fixed;
  left: 1.5rem;
  bottom: 1.5rem;
  z-index: 1070;
  width: 360px;
  max-width: calc(100vw - 1.5rem);
  background: #fff;
  border: 1px solid #e8eaef;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(16, 24, 40, 0.16);
  overflow: hidden;
  color: #101828;
}
.tray-section + .tray-section {
  border-top: 1px solid #eef0f3;
}
.tray-head {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem 0.5rem 0.75rem 0.875rem;
}
.tray-title {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
}
.tray-muted {
  font-size: 0.75rem;
  color: #5b6472;
}
.tray-action {
  min-height: 32px;
  padding: 0 0.75rem;
  border: 0;
  border-radius: 999px;
  background: #eef5fe;
  color: var(--primary, #0b63c5);
  font-size: 0.8125rem;
  white-space: nowrap;
}
.tray-action.danger {
  background: #fdf0ee;
  color: #8f1d14;
  font-weight: 600;
}
.tray-action:hover,
.tray-icon:hover {
  background: #eef1f5;
}
.tray-icon {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: #5b6472;
}
.tray-progress {
  height: 4px;
  background: #eef0f3;
}
.tray-progress-bar {
  height: 100%;
  border-radius: 0 4px 4px 0;
  background: var(--primary, #0b63c5);
  transition: width 0.2s;
}
.tray-rows {
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  max-height: 240px;
  overflow-y: auto;
}
.tray-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  font-size: 0.8125rem;
}
.tray-row-dot {
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: 4px;
  background: #c3c9d4;
}
.tray-row-dot.is-uploading,
.tray-row-dot.is-uploaded {
  background: var(--primary, #0b63c5);
}
.tray-row-dot.is-done {
  background: #1d6b3a;
}
.tray-row-dot.is-failed {
  background: #b42318;
}
.tray-row-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tray-row-status {
  font-size: 0.75rem;
  font-weight: 600;
  color: #5b6472;
  white-space: nowrap;
}
.tray-row-status.is-done {
  color: #1d6b3a;
}
.tray-row-status.is-failed {
  color: #b42318;
}
.tray-row-retry {
  min-height: 28px;
  padding: 0 0.75rem;
  border: 1px solid #d7dbe3;
  border-radius: 999px;
  background: #fff;
  font-size: 0.75rem;
}

@media (max-width: 575.98px) {
  /* เหนือแถบปุ่มล่างของหน้ารูป/เอกสาร ไม่บังปุ่มถ่ายรูประหว่างอัปโหลด */
  .transfer-tray {
    left: 0.75rem;
    right: 0.75rem;
    bottom: calc(5.5rem + env(safe-area-inset-bottom));
    width: auto;
    max-width: none;
  }
}
</style>
